/**
 * Subscription Management API Client
 *
 * Typed frontend client for all /v1/subscriptions/* endpoints.
 * Mirrors the style of billing.ts — uses apiFetch from client.ts.
 */

import { apiFetch } from "./client";
import type { PlanName, PlanLimits } from "./billing";

// ── Types ─────────────────────────────────────────────────────────────────────

export type { PlanName, PlanLimits };

export interface GracePeriodInfo {
  downgraded_at: string | null;
  grace_ends_at: string | null;
  days_remaining: number | null;
  in_grace_period: boolean;
}

export interface SubscriptionOverview {
  org: { name: string; slug: string; created_at: string };
  plan: PlanName;
  limits: PlanLimits;
  subscription: {
    status: string;
    currentPeriodEnd: string | null;
    trialEndsAt: string | null;
    cancelAtPeriodEnd: boolean;
    planCode: string | null;
    billingEmail: string | null;
    hasCustomer: boolean;
    hasSubscription: boolean;
    billingModel?: "one_time" | "subscription";
  };
  usage: {
    repos: { current: number; limit: number };
    members: { current: number; limit: number };
    scansThisMonth: { current: number; limit: number };
  };
  grace: GracePeriodInfo;
}

export interface PlanChangeEvent {
  id: string;
  from_plan: PlanName;
  to_plan: PlanName;
  changed_by: string;
  paystack_event: string | null;
  notes: string | null;
  changed_at: string;
}

export interface UsageSnapshot {
  snapshot_date: string;
  repos_count: number;
  members_count: number;
  scans_count: number;
}

export interface AnalyticsSummary {
  totalRepos: number;
  totalMembers: number;
  scansTotal: number;
  scansThisMonth: number;
  peakDay: { date: string; scans: number } | null;
}

export interface UsageAnalytics {
  snapshots: UsageSnapshot[];
  summary: AnalyticsSummary;
}

export interface SeatMember {
  id: string;
  github_login: string;
  github_name: string | null;
  github_avatar_url: string | null;
  role: "admin" | "member" | "viewer";
  joined_at: string;
}

export interface SeatsResponse {
  members: SeatMember[];
  seats: { current: number; limit: number };
  plan: PlanName;
}

export interface EntitlementFeature {
  id: string;
  label: string;
  unlocked: boolean;
  available_on: PlanName[];
}

export interface EntitlementsResponse {
  plan: PlanName;
  matrix: EntitlementFeature[];
}

export interface InviteLinkResponse {
  invite_url: string;
  expires_at: string;
}

// ── API Functions ─────────────────────────────────────────────────────────────

export async function getSubscriptionOverview(): Promise<SubscriptionOverview> {
  return apiFetch<SubscriptionOverview>({ path: "/v1/subscriptions/overview" });
}

export async function getPlanHistory(): Promise<{ history: PlanChangeEvent[] }> {
  return apiFetch<{ history: PlanChangeEvent[] }>({ path: "/v1/subscriptions/history" });
}

export async function getUsageAnalytics(): Promise<UsageAnalytics> {
  return apiFetch<UsageAnalytics>({ path: "/v1/subscriptions/analytics" });
}

export async function getSeats(): Promise<SeatsResponse> {
  return apiFetch<SeatsResponse>({ path: "/v1/subscriptions/seats" });
}

export async function removeSeat(userId: string): Promise<void> {
  return apiFetch<void>({ method: "DELETE", path: `/v1/subscriptions/seats/${userId}` });
}

export async function getInviteLink(): Promise<InviteLinkResponse> {
  return apiFetch<InviteLinkResponse>({ path: "/v1/subscriptions/invite-link" });
}

export interface JoinInviteResponse {
  joined: boolean;
  already_member: boolean;
  org: { name: string; slug: string };
  role: string;
}

/** Redeem an invite token generated by "Generate Link" (see /join). */
export async function joinOrgByInvite(token: string, org?: string): Promise<JoinInviteResponse> {
  return apiFetch<JoinInviteResponse>({
    method: "POST",
    path: "/v1/subscriptions/join",
    body: { token, org },
  });
}

export async function getDowngradeStatus(): Promise<GracePeriodInfo> {
  return apiFetch<GracePeriodInfo>({ path: "/v1/subscriptions/downgrade-status" });
}

export async function getEntitlements(): Promise<EntitlementsResponse> {
  return apiFetch<EntitlementsResponse>({ path: "/v1/subscriptions/entitlements" });
}

// ── Critical Feature API Functions ───────────────────────────────────────────

export interface AlertConfig {
  threshold_pct: number;
  notify_email: string;
  enabled: boolean;
}

export interface UsageAlerts {
  repos:         AlertConfig;
  members:       AlertConfig;
  scansThisMonth:AlertConfig;
}

export interface AuditEvent {
  id: string;
  event_type: string;
  event_action: string;
  actor_type: string;
  actor_id: string | null;
  resource_type: string;
  details: Record<string, unknown>;
  created_at: string;
  source: "audit_log" | "billing_events";
}

export interface AuditLogResponse {
  events: AuditEvent[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
  eventTypes: string[];
}

export interface MonthlyBand {
  month: string;
  amount_paid: number;
  currency: string;
  invoice_count: number;
}

export interface SpendSummary {
  currency: string;
  ytd: number;
  lastMonth: number;
  avgMonthly: number;
  projectedAnnual: number;
  invoicesPaid: number;
  invoicesFailed: number;
  monthlyBreakdown: MonthlyBand[];
}

export async function getUsageAlerts(): Promise<{ alerts: UsageAlerts }> {
  return apiFetch<{ alerts: UsageAlerts }>({ path: "/v1/subscriptions/alerts" });
}

export async function saveUsageAlerts(alerts: Partial<UsageAlerts>): Promise<void> {
  return apiFetch<void>({ method: "PUT", path: "/v1/subscriptions/alerts", body: alerts });
}

export async function getAuditLog(params: {
  page?: number; limit?: number; type?: string;
  from?: string; to?: string; search?: string;
}): Promise<AuditLogResponse> {
  const qs = new URLSearchParams();
  if (params.page)   qs.set("page",   String(params.page));
  if (params.limit)  qs.set("limit",  String(params.limit));
  if (params.type)   qs.set("type",   params.type);
  if (params.from)   qs.set("from",   params.from);
  if (params.to)     qs.set("to",     params.to);
  if (params.search) qs.set("search", params.search);
  return apiFetch<AuditLogResponse>({ path: `/v1/subscriptions/audit-log?${qs.toString()}` });
}

export async function getSpendSummary(): Promise<SpendSummary> {
  return apiFetch<SpendSummary>({ path: "/v1/subscriptions/spend-summary" });
}

export async function updateRenewal(payload: {
  cancel_at_period_end: boolean;
  reminder_days?: number | null;
}): Promise<{ ok: boolean; cancel_at_period_end: boolean; message: string }> {
  return apiFetch({ method: "POST", path: "/v1/subscriptions/renewal", body: payload });
}

// ── High-Priority Feature API Functions ──────────────────────────────────────

export interface BillingContact {
  billing_email:    string;
  billing_cc:       string[];
  company_name:     string;
  vat_number:       string;
  tax_id:           string;
  phone:            string;
  address_line1:    string;
  address_city:     string;
  address_country:  string;
}

export interface BillingMetadata {
  cost_center:        string;
  po_number:          string;
  internal_reference: string;
  fiscal_year_start:  number;  // 1-12
  department:         string;
  budget_code:        string;
}

export interface SSOConfig {
  idp_metadata_url: string | null;
  idp_entity_id:    string | null;
  enabled:          boolean;
  last_tested_at:   string | null;
  test_status:      "success" | "failed" | null;
  test_error:       string | null;
}

export interface SSOConfigResponse {
  config: SSOConfig;
  // `available` is false until a SAML handler is actually deployed: the SP URLs
  // are the addresses those endpoints would use, not live endpoints.
  sp: { entity_id: string; acs_url: string; slo_url: string; available?: boolean };
  plan: string;
}

// Billing Contact
export async function getBillingContact(): Promise<{ contact: BillingContact }> {
  return apiFetch<{ contact: BillingContact }>({ path: "/v1/subscriptions/billing-contact" });
}

export async function saveBillingContact(contact: Partial<BillingContact>): Promise<void> {
  return apiFetch<void>({ method: "PUT", path: "/v1/subscriptions/billing-contact", body: contact });
}

// Cost Center / PO
export async function getBillingMetadata(): Promise<{ metadata: BillingMetadata }> {
  return apiFetch<{ metadata: BillingMetadata }>({ path: "/v1/subscriptions/billing-metadata" });
}

export async function saveBillingMetadata(metadata: Partial<BillingMetadata>): Promise<void> {
  return apiFetch<void>({ method: "PUT", path: "/v1/subscriptions/billing-metadata", body: metadata });
}

// SSO / SAML
export async function getSSOConfig(): Promise<SSOConfigResponse> {
  return apiFetch<SSOConfigResponse>({ path: "/v1/subscriptions/sso-config" });
}

export async function saveSSOConfig(config: Partial<Pick<SSOConfig, "idp_metadata_url" | "idp_entity_id" | "enabled">>): Promise<void> {
  return apiFetch<void>({ method: "PUT", path: "/v1/subscriptions/sso-config", body: config });
}

export async function testSSOConfig(idp_metadata_url: string): Promise<{
  reachable: boolean;
  status_code: number | null;
  error: string | null;
  tested_at: string;
}> {
  return apiFetch({ method: "POST", path: "/v1/subscriptions/sso-config/test", body: { idp_metadata_url } });
}

